import type { CatalogPriceZoneSPPriceFamily, SelectedProductsAndSellingPrices } from 'hooks/catalog-price-zone-service-provider';
import type { FormInstance } from 'antd/es/form';
import type { OrderPrestataireLine } from 'components/Logistique/LogistiqueOrderDetail/OrderPrestataireCellTable';
import type { FormValues } from 'components/Logistique/LogistiqueOrderDetail/OrderPrestataireCellTable/PriceValueCell';
import { PRICE_TYPES } from 'utils/constant';
import type { PriceType } from 'models';
import { handleCalculateUnitPrix } from 'components/Logistique/LogistiqueOrderDetail/OrderPrestataireCellTable/GenerateCatalogPriceSection';

/**
 * Retrieves the selling price for a product, with special handling for "Transport Benne" products.
 * @param item - The product details, including product ID, name, and optional selling unit price.
 * @param selectedProductsFromOrderClient - Array of client-selected products with their selling prices.
 * @returns The selling price for the product, prioritizing "Transport Benne : Pose et enlèvement" price if applicable.
 * 
 * This function first checks for the product's selling price in the client-selected products.
 * If the product is a "Transport Benne" type (Pose, Enlèvement, or Rotation), it looks for a related
 * "Transport Benne : Pose et enlèvement" product and uses its price if available. Otherwise, it falls
 * back to the item's selling price or 0.
 */
export const getSellingPrice = (
  item: {
    productId?: number;
    productName?: string;
    sellingUnitPrice?: number;
    documentProductLineId?: number;
  },
  selectedProductsFromOrderClient: SelectedProductsAndSellingPrices[]
): number => {
  const selectedProductFromOrderClient = selectedProductsFromOrderClient.find(
    (sp) => sp.productId === item.productId && sp.documentProductLineId === item.documentProductLineId
  );

  let sellingPrice = selectedProductFromOrderClient?.sellingUnitPrice ?? item.sellingUnitPrice ?? 0;

  if (
    item.productName &&
    (/Transport Benne : Pose - .+/.test(item.productName) ||
      /Transport Benne : Enlèvement - .+/.test(item.productName) ||
      /Transport Benne : Rotation - .+/.test(item.productName))
  ) {
    const benneTransportProduct = selectedProductsFromOrderClient.find(
      (sp) => sp.productName && /Transport Benne : Pose et enlèvement - .+/.test(sp.productName)
    );
    sellingPrice = benneTransportProduct?.sellingUnitPrice ?? sellingPrice;
  }

  return sellingPrice;
};

/**
 * Calculates the margin percentage between the client price and service provider price.
 * @param clientPrice - The price charged to the client.
 * @param spPrice - The price from the service provider.
 * @param isEnlevementTransport - Indicates if the product is a "Transport Benne" type (Pose, Enlèvement, or Rotation).
 * @returns The margin percentage as a string, rounded to 2 decimal places.
 * 
 * The margin is calculated as ((clientPrice - spPrice) / clientPrice) * 100.
 * If the product is a "Transport Benne" type and isEnlevementTransport is true, the margin is divided by 2.
 * Returns '0' if clientPrice is 0 or falsy to avoid division by zero.
 */
export const calculateMargin = (
  clientPrice: number,
  spPrice: number,
  isEnlevementTransport: boolean = false
): string => {
  if (!clientPrice || clientPrice === 0) return '0';

  let margin = ((clientPrice - spPrice) / clientPrice) * 100;

  if (isEnlevementTransport) {
    margin /= 2;
  }

  return margin.toFixed(2);
};

/**
 * Computes the margin for a product based on form data and product details, with special handling for "Transport Benne" products.
 * @param form - Ant Design FormInstance for accessing form data.
 * @param formValuesWatch - Form values being watched, used to compute the service provider price.
 * @param record - The service provider order line data.
 * @param item - The product details, including UUID, product ID, name, price families, and selected price family ID.
 * @param priceTypes - Array of price types to identify the quantity price type.
 * @param productListFromOrderClient - Array of client-selected products with their selling prices.
 * @returns The calculated margin percentage as a string.
 * 
 * This function calculates the service provider's unit price using handleCalculateUnitPrix.
 * It then determines the client's unit price, checking for "Transport Benne" products (Pose, Enlèvement, or Rotation).
 * If a "Transport Benne" product is detected, it uses the price of a "Transport Benne : Pose et enlèvement" product
 * and sets isEnlevementTransport to true. The margin is computed using calculateMargin, halving it for "Transport Benne" products if applicable.
 */
export const getMargin = (
  form: FormInstance,
  formValuesWatch: FormValues,
  record: OrderPrestataireLine,
  item: {
    uuid?: string;
    productId?: number;
    productName?: string;
    priceFamilies?: CatalogPriceZoneSPPriceFamily[];
    selectedPriceFamilyId?: number | null;
    sellingUnitPrice?: number;
  },
  priceTypes: PriceType[],
  productListFromOrderClient: SelectedProductsAndSellingPrices[]
): string => {
  const qtyPriceType = priceTypes?.find((priceType) => priceType.key === PRICE_TYPES.numberInput);

  const unitPriceFromOrderSP = handleCalculateUnitPrix({
    form,
    formValuesWatch,
    record,
    uuid: item?.uuid,
    productId: item?.productId,
    priceFamilies: item?.priceFamilies ?? [],
    selectedPriceFamilyId: item?.selectedPriceFamilyId,
    qtyPriceType,
  });

  let unitPriceFromOrderClient = productListFromOrderClient.find((sp) => sp.productId === item.productId)?.sellingUnitPrice ?? 0;
  let isEnlevementTransport = false;

  if (
    item.productName &&
    (/Transport Benne : Pose - .+/.test(item.productName) ||
      /Transport Benne : Enlèvement - .+/.test(item.productName) ||
      /Transport Benne : Rotation - .+/.test(item.productName))
  ) {
    const benneTransportProduct = productListFromOrderClient.find((sp) => sp.isBenne && sp.isTransport);
    if (benneTransportProduct) {
      unitPriceFromOrderClient = benneTransportProduct.sellingUnitPrice ?? 0;
      isEnlevementTransport = true;
    }
  }

  return calculateMargin(unitPriceFromOrderClient, unitPriceFromOrderSP, isEnlevementTransport);
};